/**
 * Modal that appears when simulation time limit has been reached
 * V5 styled version with Tailwind and Framer Motion
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Timer } from 'lucide-react';

interface TimeExpiredModalProps {
    elapsedMinutes: number;
    timeLimitMinutes: number;
    onViewEvaluation: () => void;
    isLoading?: boolean;
}

export function TimeExpiredModal({
    elapsedMinutes,
    timeLimitMinutes,
    onViewEvaluation,
    isLoading = false
}: TimeExpiredModalProps) {

    const formatTime = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);

        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins} minutes`;
    };

    return (
        <>
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-red-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Timer className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">
                                    Time Limit Reached
                                </h2>
                                <p className="text-sm text-slate-600 mt-0.5">
                                    The simulation time has expired
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6 space-y-4">

                        {/* Time Summary */}
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-slate-600 mb-1">Time Allocated</p>
                                    <p className="text-lg font-semibold text-slate-800">
                                        {formatTime(timeLimitMinutes)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-600 mb-1">Time Elapsed</p>
                                    <p className="text-lg font-semibold text-orange-600">
                                        {formatTime(elapsedMinutes)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Message */}
                        <div className="space-y-3">
                            <p className="text-slate-700 leading-relaxed">
                                The allocated time for this simulation has been reached. The input has been disabled and you can no longer send messages.
                            </p>

                            <p className="text-slate-700 leading-relaxed">
                                Click the button below to view your evaluation results and see how you performed during the simulation.
                            </p>
                        </div>

                        {/* Note */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                            <p className="text-sm text-emerald-800">
                                <strong>Note:</strong> Your performance is evaluated based on actions taken within the allocated time period.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                        <button
                            onClick={onViewEvaluation}
                            disabled={isLoading}
                            className="w-full px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <motion.div
                                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    />
                                    Generating Evaluation...
                                </>
                            ) : (
                                'View Evaluation Results'
                            )}
                        </button>
                    </div>
                </div>
            </motion.div>
        </>
    );
}

export default TimeExpiredModal;

